/** @file axisLines: the +X (east, red) and +Y (north, green) pose axis lines on the scene's ground. */
import { Color4 } from '@babylonjs/core/Maths/math.color.js';
import { Vector3 } from '@babylonjs/core/Maths/math.vector.js';
import { CreateLineSystem } from '@babylonjs/core/Meshes/Builders/linesBuilder.js';
import type { LinesMesh } from '@babylonjs/core/Meshes/linesMesh.js';
import type { Scene } from '@babylonjs/core/scene.js';
import { VIEWPORT_COLORS } from './viewportColors';

/** Axis length in units (the grid's half extent) and the lift above the grid that keeps them on top of it. */
const LENGTH = 20;
const LIFT = 0.002;

/**
 * Babylon x (pose +x, east) red and Babylon z (pose +y, north) green: named and coloured by the pose axes (the scene is
 * y up only internally), full length through the origin, the negative half at 40 % alpha. The `+X` / `+Y` labels are
 * drawn by the HUD (`OrientationCube`'s ground ring), not in the scene.
 */
export function axisLines(scene: Scene): LinesMesh {
  const x = Color4.FromHexString(`${VIEWPORT_COLORS.axisX}ff`);
  const y = Color4.FromHexString(`${VIEWPORT_COLORS.axisY}ff`);
  const dim = (c: Color4): Color4 => new Color4(c.r, c.g, c.b, 0.4);
  const lines = [
    [new Vector3(0, LIFT, 0), new Vector3(LENGTH, LIFT, 0)], [new Vector3(-LENGTH, LIFT, 0), new Vector3(0, LIFT, 0)],
    [new Vector3(0, LIFT, 0), new Vector3(0, LIFT, LENGTH)], [new Vector3(0, LIFT, -LENGTH), new Vector3(0, LIFT, 0)],
  ];
  const colors = [[x, x], [dim(x), dim(x)], [y, y], [dim(y), dim(y)]];
  const mesh = CreateLineSystem('viewport-axes', { lines, colors, useVertexAlpha: true }, scene);
  mesh.isPickable = false;
  return mesh;
}
